'use client';

// UrlCrawlProgress - Shows progress during multi-page URL extraction

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { useTranslations } from 'next-intl';
import type { ExtractionSummary } from '../../hooks/useUrlExtraction';

export interface UrlCrawlProgressProps {
  /** Whether extraction is in progress */
  isExtracting: boolean;
  /** Extraction summary (when complete) */
  summary?: ExtractionSummary | null;
  /** Number of URLs currently being processed */
  currentCount?: number;
  /** Total number of URLs expected */
  totalCount?: number;
}

/**
 * UrlCrawlProgress - Shows extraction progress for multi-page URL crawling
 *
 * Features:
 * - Linear progress bar during extraction
 * - Page count progress (e.g., "3/10 pages")
 * - Summary when complete (discovered vs succeeded)
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <UrlCrawlProgress
 *   isExtracting={isExtracting}
 *   summary={summary}
 *   currentCount={3}
 *   totalCount={10}
 * />
 * ```
 */
export function UrlCrawlProgress({
  isExtracting,
  summary,
  currentCount = 0,
  totalCount = 0,
}: UrlCrawlProgressProps) {
  const t = useTranslations('creator.urlExtraction');

  // Don't show anything if not extracting and no summary
  if (!isExtracting && !summary) {
    return null;
  }

  // Don't show if extraction complete but no links were discovered (single page URL)
  if (!isExtracting && summary && summary.linksDiscovered === 0) {
    return null;
  }

  // Calculate progress percentage
  const progress = totalCount > 0 ? (currentCount / totalCount) * 100 : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderRadius: 1,
        backgroundColor: 'action.hover',
      }}
    >
      {isExtracting ? (
        // Extraction in progress
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {totalCount > 1
                ? t('crawl.progress', { current: currentCount, total: totalCount })
                : t('input.fetching')}
            </Typography>
          </Box>
          <LinearProgress
            variant={totalCount > 1 ? 'determinate' : 'indeterminate'}
            value={progress}
            sx={{ borderRadius: 1 }}
          />
        </>
      ) : summary ? (
        // Extraction complete - show summary
        <>
          {summary.linksDiscovered > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('crawl.discovered', { count: summary.linksDiscovered })}
            </Typography>
          )}
          {summary.linksCrawled > 0 && summary.linksSucceeded < summary.linksCrawled && (
            <Typography variant="body2" color="warning.main">
              {t('crawl.partialSuccess', {
                succeeded: summary.linksSucceeded,
                total: summary.linksCrawled,
              })}
            </Typography>
          )}
        </>
      ) : null}
    </Box>
  );
}
